import * as React from 'react'

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import CalendarDayViewStore from '~/client/src/shared/components/CalendarDayView/CalendarDayView.store'
import FormMultiDayLabel from '~/client/src/shared/components/CalendarDayView/FormMultiDayEventLabel/FormMultiDayEventLabel'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CalendarEvent from '~/client/src/shared/models/CalendarEvent'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import './MultiDayEventsBar.scss'

const COLLAPSE_THRESHOLD = 4

interface IProps {
  viewingDate: Date
  setViewingDate: (date: Date) => void
  events: CalendarEvent[]
  calendarStore: CalendarDayViewStore

  projectDateStore?: ProjectDateStore
  onEventClicked: (event: CalendarEvent) => void
}

@inject('projectDateStore')
@observer
export default class MultiDayEventsBar extends React.Component<IProps> {
  @observable private isExpanded: boolean = false

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const {
      viewingDate,
      calendarStore,
      projectDateStore: { isSameDay },
    } = this.props

    const allEvents = calendarStore.getActiveMultiDayEvents(this.props.events)

    const events = this.isExpanded
      ? allEvents
      : allEvents.slice(0, COLLAPSE_THRESHOLD)

    if (!events.length) return null

    return (
      <div className="multi-day-events-container col y-start bg-white pt5">
        <div className="overflow-y-auto">
          {events.map((event, idx) => {
            const isStartDate = isSameDay(event.startDate, viewingDate)
            const isEndDate = isSameDay(event.endDate, viewingDate)

            return (
              <div
                key={event.dataId || idx}
                onClick={() => this.props.onEventClicked(event)}
                style={event.styles}
                className={classList({
                  'multi-day-event pl5 mb2 text light row nowrap overflow-hidden': true,
                  brls4: isStartDate,
                  brrs4: isEndDate,
                  'extends-left': !isStartDate,
                  'extends-right': !isEndDate,
                  draft: event.data?.isDraft,
                  opacity7: event.data?.isDone && !event.isShared,
                  opacity6: event.data?.isDeniedOrFailed && !event.isShared,
                  'shared-event': event.isShared,
                })}
              >
                <FormMultiDayLabel event={event} iconClassName="w18" />
              </div>
            )
          })}
        </div>

        {this.renderFooter(allEvents.length)}
      </div>
    )
  }

  private renderFooter(eventsCount: number): JSX.Element {
    const shouldShowCollapse = eventsCount > COLLAPSE_THRESHOLD

    if (!shouldShowCollapse) return null

    const hiddenCount = eventsCount - COLLAPSE_THRESHOLD
    const ChevronIcon = this.isExpanded ? ChevronUpIcon : ChevronDownIcon

    return (
      <div
        className="collapse-container pointer mt5"
        onClick={this.toggleExpand}
      >
        <ChevronIcon size={16} className="tw:mr-1" />
        {this.isExpanded
          ? Localization.translator.showLess
          : Localization.translator.showXMore(hiddenCount)}
      </div>
    )
  }

  @action.bound
  private toggleExpand() {
    this.isExpanded = !this.isExpanded
  }
}
